import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { UserManager, type User } from "oidc-client-ts";
import { i18n } from "@/i18n";
import { routePolicy } from "@/api/policy.generated";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuthUser {
  sub: string;
  email: string;
  tenantId: string;
  role: string;
  groups: string[];
}

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN || "";
const COGNITO_CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID || "";
const COGNITO_USER_POOL_ID = import.meta.env.VITE_COGNITO_USER_POOL_ID || "";
const COGNITO_REDIRECT_URI =
  import.meta.env.VITE_COGNITO_REDIRECT_URI ||
  `${window.location.origin}/auth/callback`;
const COGNITO_LOGOUT_URI =
  import.meta.env.VITE_COGNITO_LOGOUT_URI || `${window.location.origin}/`;

// Cognito OIDC issuer URL — this is where /.well-known/openid-configuration lives.
// The hosted UI domain (COGNITO_DOMAIN) does NOT serve the discovery document.
// Pool ID format is "{region}_{suffix}", so we split on the first underscore to get the region.
const COGNITO_AUTHORITY = COGNITO_USER_POOL_ID
  ? `https://cognito-idp.${COGNITO_USER_POOL_ID.split("_")[0]}.amazonaws.com/${COGNITO_USER_POOL_ID}`
  : "";

// ---------------------------------------------------------------------------
// UserManager (module-level singleton — not reactive state)
// ---------------------------------------------------------------------------

const userManager = new UserManager({
  // OIDC issuer URL for the Cognito user pool. oidc-client-ts fetches
  // {authority}/.well-known/openid-configuration from here (not from the
  // Hosted UI domain, which returns 404 for that path).
  authority: COGNITO_AUTHORITY,
  client_id: COGNITO_CLIENT_ID,
  redirect_uri: COGNITO_REDIRECT_URI,
  post_logout_redirect_uri: COGNITO_LOGOUT_URI,
  scope: "openid email profile",

  // Cognito omits end_session_endpoint from its OIDC discovery document.
  // metadataSeed merges into (not replaces) the discovered metadata, so
  // all other endpoints are still auto-discovered.
  metadataSeed: {
    end_session_endpoint: `${COGNITO_DOMAIN}/logout`,
  },

  // Automatically renew the access token before it expires. When a refresh
  // token is present the library uses the token endpoint directly (refresh
  // token grant) rather than a hidden iframe. This matters for Cognito:
  // Cognito Hosted UI sets X-Frame-Options: DENY, which would block the
  // iframe-based prompt=none flow. No /silent-renew.html file is needed.
  automaticSilentRenew: true,

  // Keep all access token claims in user.profile, including `sub` and
  // cognito:groups (which carries both role and tenant information).
  filterProtocolClaims: false,

  // sessionStorage (library default): tokens survive page refreshes within
  // the same tab and are cleared when the tab closes. Better UX than pure
  // JS memory (redirect on every refresh) without the persistence risks of
  // localStorage (tokens survive browser restarts and are on disk).
});

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useAuthStore = defineStore("auth", () => {
  const user = ref<AuthUser | null>(null);
  const isLoading = ref(false);
  const authError = ref<string | null>(null);
  const isAuthenticated = computed(() => user.value !== null);

  // Group-membership flags. Roles are composable — a user may hold several
  // groups, and each capability gates on exactly one group. These read the raw
  // `groups` array (not the precedence `role` string) so combined roles work.
  const isUser = computed(
    () => user.value?.groups.includes("role_user") ?? false,
  );
  const isInstructor = computed(
    () => user.value?.groups.includes("role_instructor") ?? false,
  );
  const isAdmin = computed(
    () => user.value?.groups.includes("role_admin") ?? false,
  );

  // can reports whether the current user may invoke the given API operation,
  // driven by the spec-generated routePolicy (the same source the backend
  // enforces). An operation absent from the policy requires only authentication;
  // a listed operation needs at least one of its groups (any-of).
  function can(operationId: string): boolean {
    const need = routePolicy[operationId];
    if (!need || need.length === 0) return isAuthenticated.value;
    const groups = user.value?.groups ?? [];
    return need.some((g) => groups.includes(g));
  }

  function toAuthUser(oidcUser: User): AuthUser {
    const p = oidcUser.profile;
    const groups = (p["cognito:groups"] as string[]) ?? [];
    const role = groups.includes("role_admin")
      ? "admin"
      : groups.includes("role_instructor")
        ? "instructor"
        : "user";
    const tenantGroup = groups.find((g) => g.startsWith("tenant_"));
    const tenantId = tenantGroup ? tenantGroup.slice("tenant_".length) : "";
    return {
      sub: p.sub,
      email: (p.email as string) || "",
      tenantId,
      role,
      groups,
    };
  }

  // Keep store state in sync when the library loads or expires tokens in the
  // background (automaticSilentRenew fires these events).
  userManager.events.addUserLoaded((oidcUser) => {
    user.value = toAuthUser(oidcUser);
  });
  userManager.events.addUserUnloaded(() => {
    user.value = null;
  });

  /**
   * Restore auth state from sessionStorage on app bootstrap.
   * Must be awaited before mounting so the router guard sees the correct
   * isAuthenticated value on the first navigation.
   */
  async function initAuth(): Promise<void> {
    const oidcUser = await userManager.getUser();
    if (oidcUser && !oidcUser.expired) {
      user.value = toAuthUser(oidcUser);
    }
  }

  /**
   * Start the OIDC Authorization Code + PKCE flow.
   * Redirects to Cognito Hosted UI. oidc-client-ts generates the PKCE
   * verifier/challenge and stores state in sessionStorage automatically.
   *
   * The current pathname + search is stored in the OIDC `state` so that
   * handleCallback() can redirect back to the originating page after login
   * instead of always landing on "/".
   */
  async function login(): Promise<void> {
    try {
      const supportedLangs = [
        "de",
        "en",
        "es",
        "fr",
        "id",
        "nl",
        "it",
        "ja",
        "ko",
        "pt-BR",
        "zh-CN",
        "zh-TW",
      ];
      const locale = i18n.global.locale.value;
      const lang = supportedLangs.includes(locale) ? locale : "en";
      const returnTo = window.location.pathname + window.location.search;
      await userManager.signinRedirect({
        extraQueryParams: { lang },
        state: returnTo,
      });
    } catch (e) {
      authError.value =
        e instanceof Error ? e.message : "Authentication configuration error";
      throw e;
    }
  }

  /**
   * Complete the OIDC callback. Reads code and state from window.location
   * automatically — no need to extract them from the URL manually.
   *
   * Returns the original URL stored in the OIDC state so the caller can
   * redirect the user back to where they were before the login redirect.
   * Falls back to "/" when no state is present.
   */
  async function handleCallback(): Promise<string> {
    isLoading.value = true;
    try {
      const oidcUser = await userManager.signinRedirectCallback();
      user.value = toAuthUser(oidcUser);
      const returnTo =
        typeof oidcUser.state === "string" && oidcUser.state
          ? oidcUser.state
          : "/";
      return returnTo;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Return the current access token. With automaticSilentRenew enabled the
   * library keeps it fresh; this is just a passthrough to the stored value.
   */
  async function getAccessToken(): Promise<string> {
    const oidcUser = await userManager.getUser();
    return oidcUser?.access_token ?? "";
  }

  /**
   * Revoke tokens, clear local session, and redirect to Cognito logout.
   *
   * We do not call signoutRedirect() here because Cognito's logout endpoint
   * uses non-standard parameters (client_id + logout_uri) instead of the
   * OIDC-standard id_token_hint + post_logout_redirect_uri. Calling
   * signoutRedirect() would send the wrong parameters and leave the Cognito
   * session active. We revoke tokens, remove the local user, and redirect
   * manually instead.
   *
   * revokeTokens() calls Cognito's /oauth2/revoke endpoint to invalidate the
   * refresh token server-side. The access token is a short-lived JWT validated
   * locally by API Gateway — Cognito has no revocation endpoint for it.
   */
  async function logout(): Promise<void> {
    // Cognito's revoke endpoint only accepts refresh tokens; attempting to
    // revoke the access token returns 400. Treat failure as non-fatal —
    // the /logout redirect below ends the Cognito SSO session regardless.
    try {
      await userManager.revokeTokens(["refresh_token"]);
    } catch {
      // ignore — session is ended server-side by the redirect below
    }
    await userManager.removeUser();
    const params = new URLSearchParams({
      client_id: COGNITO_CLIENT_ID,
      logout_uri: COGNITO_LOGOUT_URI,
    });
    window.location.href = `${COGNITO_DOMAIN}/logout?${params}`;
  }

  return {
    user,
    isLoading,
    authError,
    isAuthenticated,
    isUser,
    isInstructor,
    isAdmin,
    can,
    initAuth,
    login,
    logout,
    handleCallback,
    getAccessToken,
  };
});
